import type { TableOfContentsItemFormFieldFragment } from "../../client/generated";
import type { ComponentProps } from "react";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  FormControl,
  FormField,
  FormLabel,
} from "@acdc2/ui/components/access-form";
import { RegionSelectContent } from "../../regions/RegionSelectContent";
import {
  Select,
  SelectTrigger,
  SelectValue,
} from "@acdc2/ui/components/select";

type Props = Omit<
  ComponentProps<typeof RegionSelectContent>,
  "qualifyingAttributes"
> & {
  fragment: TableOfContentsItemFormFieldFragment;
};

export function TableOfContentsItemFormField({
  fragment,
  value,
  onOpenChange,
  ...props
}: Props) {
  const { t } = useTranslation();
  const currentPageGroupRef = useRef<HTMLDivElement>(null);

  const selectedPage = fragment.document.pages.pages.find((page) =>
    page.regions.some((region) => region.id === value),
  );

  const handleOpenChange = (isOpen: boolean) => {
    onOpenChange?.(isOpen);

    if (isOpen && !selectedPage) {
      setTimeout(() => {
        currentPageGroupRef.current?.scrollIntoView({
          block: "start",
          behavior: "smooth",
        });
      });
    }
  };

  return (
    <FormField name="tableOfContentsItem">
      <FormLabel>{t("TableOfContentsItemFormField.label")}</FormLabel>
      <Select value={value} onOpenChange={handleOpenChange} {...props}>
        <FormControl asChild>
          <SelectTrigger className="w-full">
            <SelectValue
              placeholder={t("TableOfContentsItemFormField.placeholder")}
            >
              {t("TableOfContentsItemFormField.selection", {
                page: selectedPage?.index,
              })}
            </SelectValue>
          </SelectTrigger>
        </FormControl>
        <RegionSelectContent
          fragment={fragment}
          currentPageGroupRef={currentPageGroupRef}
          qualifyingAttributes={["HeaderLevelAttribute", "CaptionAttribute"]}
        />
      </Select>
    </FormField>
  );
}
